import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Collapse, Tooltip, Typography } from '@mui/material';
import { Icon } from '@sdb/planning-ui';
import DOMPurify from 'dompurify';
import {
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  useGridApiRef,
  GRID_DETAIL_PANEL_TOGGLE_FIELD
} from '@mui/x-data-grid-pro';
import { useTranslation } from 'react-i18next';
import {
  format as formatDate,
  parseISO,
  startOfMonth,
  startOfYear,
  endOfMonth,
  endOfYear
} from 'date-fns';
import { useQueryClient } from 'react-query';

import { useSelector } from 'hooks';
import { DataGrid } from 'components/shared/DataGrid';
import LoadingOverlay from 'components/shared/LoadingOverlay';
import { ToolbarWrapper } from 'components/shared/ToolbarSet/ToolbarWrapper';
import { SingleOrRange } from 'components/shared/DateNavigation';
import { useDateNavigationStore } from 'components/shared/DateNavigation/DateNavigation.stores';
import { PlanUnitMix } from 'components/types';
import { flattenToDate } from 'utils/date';
import useGetPlannerTasks from './hooks/useGetPlannerTasks';
import useDeletePlannerTask from './hooks/useDeletePlannerTask';
import { usePlannerMessagesStore } from './store';
import {
  BULK_DISABLED_TABS,
  PAGE_SIZE,
  REJECTION_TASK_KEYS
} from './constants';
import { ParsedMessageData, PlannerMessage, PlannerTaskTab } from './types';
import parseMessageData from './parseMessageData';
import TaskTypeTabs from './components/TaskTypeTabs';
import MessageActionButtons from './components/MessageActionButtons';
import { ActionButton } from './components/MessageActionButtons.styles';
import {
  EmployeeAvatar,
  EmployeeCell,
  ActionsCell,
  NoteText
} from './components/MessageRow.styles';
import {
  DetailPanelContent,
  MessagesContainer,
  GridWrapper,
  PillChip,
  PillChipGroup
} from './PlannerMessages.styles';

type ParsedPlannerMessageRow = PlannerMessage & {
  parsed: ParsedMessageData;
};

const getInitials = (name: string): string => {
  const parts = name
    .replace(/\([^)]*\)/g, '')
    .split(/[\s,]+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '?';
  return (
    (parts[0][0] ?? '') + (parts[parts.length - 1][0] ?? '')
  ).toUpperCase();
};

const AnimatedDetailPanel = ({ html }: { html: string }) => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    setOpen(true);
  }, []);
  return (
    <Collapse in={open} timeout={200}>
      <DetailPanelContent
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
      />
    </Collapse>
  );
};

const PlannerMessages = () => {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const apiRef = useGridApiRef();
  const lastUsedPlanUnits = useSelector(
    (state) => state.roster.home.qualityMonitor.lastUsedPlanunit
  );
  const {
    pageIndex,
    setPageIndex,
    tab,
    selectAllTasks,
    clearSelection,
    setDateRange,
    setCostCenterIds,
    costCenterIds,
    startDate,
    endDate
  } = usePlannerMessagesStore();

  const { data, isLoading } = useGetPlannerTasks();
  const { mutateAsync: deleteTask } = useDeletePlannerTask();
  const [messages, totalCount] = data || [[], 0];

  const parsedMessages = useMemo<ParsedPlannerMessageRow[]>(
    () =>
      messages.map((message) => ({
        ...message,
        parsed: parseMessageData(message, t)
      })),
    [messages, t]
  );

  const isBulkDisabled = BULK_DISABLED_TABS.includes(tab as never);

  const columnVisibilityModel = useMemo(() => {
    const hasValue = (field: keyof ParsedMessageData) =>
      parsedMessages.some((m) => {
        const val = m.parsed[field];
        return val !== undefined && val !== null && val !== '' && val !== '-';
      });

    return {
      [GRID_DETAIL_PANEL_TOGGLE_FIELD]: false,
      hoursDiff: tab === PlannerTaskTab.DUTY_CHANGES && hasValue('hoursDiff'),
      dutyCodeChange:
        tab === PlannerTaskTab.DUTY_CHANGES && hasValue('dutyCodeChange'),
      note: tab !== PlannerTaskTab.FLEX_NOTIFICATIONS && hasValue('note'),
      leaveType: tab === PlannerTaskTab.LEAVE_REQUESTS && hasValue('leaveType'),
      taskDefinitionKey: tab !== PlannerTaskTab.LEAVE_REQUESTS
    };
  }, [tab, parsedMessages]);

  const { period } = useDateNavigationStore();

  // Keep the range in step with the period, anchored on the store's current
  // startDate (never new Date()) so a range navigated before a remount survives.
  useEffect(() => {
    if (!period || period === 'custom') return;

    const { startDate: currentStart, endDate: currentEnd } =
      usePlannerMessagesStore.getState();
    const anchor = currentStart ? parseISO(currentStart) : new Date();
    const isYear = period === 'year';
    const nextStart = formatDate(
      isYear ? startOfYear(anchor) : startOfMonth(anchor),
      'yyyy-MM-dd'
    );
    const nextEnd = formatDate(
      isYear ? endOfYear(anchor) : endOfMonth(anchor),
      'yyyy-MM-dd'
    );

    if (nextStart !== currentStart || nextEnd !== currentEnd) {
      setDateRange(nextStart, nextEnd);
    }
  }, [period, setDateRange]);

  // Pre-fill cost center filter from the home page's plan unit selection
  useEffect(() => {
    if (lastUsedPlanUnits?.length && costCenterIds.length === 0) {
      const ids = lastUsedPlanUnits.flatMap((o) =>
        'costCenters' in o && o.costCenters
          ? o.costCenters.map((cc) => String(cc.id))
          : [String(o.id)]
      );
      setCostCenterIds(ids);
    }
    //Only has to run onces
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDateChange = useCallback(
    (value: SingleOrRange) => {
      if (!value) {
        setDateRange('', '');
        return;
      }
      if (Array.isArray(value)) {
        const [start, end] = value;
        const startDate = flattenToDate(start);
        const endDate = flattenToDate(end);
        setDateRange(
          startDate ? formatDate(startDate, 'yyyy-MM-dd') : '',
          endDate ? formatDate(endDate, 'yyyy-MM-dd') : ''
        );
      } else {
        const d = flattenToDate(value);
        if (!d) {
          setDateRange('', '');
          return;
        }
        const isYear = period === 'year';
        setDateRange(
          formatDate(isYear ? startOfYear(d) : startOfMonth(d), 'yyyy-MM-dd'),
          formatDate(isYear ? endOfYear(d) : endOfMonth(d), 'yyyy-MM-dd')
        );
      }
    },
    [setDateRange, period]
  );

  const handlePlanUnitChange = useCallback(
    (planUnits: PlanUnitMix[]) => {
      const ids = planUnits.flatMap((o) =>
        'costCenters' in o && o.costCenters
          ? o.costCenters.map((cc) => String(cc.id))
          : [String(o.id)]
      );
      setCostCenterIds(ids);
    },
    [setCostCenterIds]
  );

  const columns: GridColDef<ParsedPlannerMessageRow>[] = useMemo(
    () => [
      {
        field: 'date',
        headerName: t('plannerMessages.columns.date'),
        type: 'date',
        flex: 1.5,
        minWidth: 140,
        valueGetter: (_value: unknown, row: ParsedPlannerMessageRow) =>
          row.createTime ? new Date(row.createTime) : null,
        renderCell: ({
          row
        }: GridRenderCellParams<ParsedPlannerMessageRow>) => {
          return (
            <Typography variant="body2" noWrap>
              {row.parsed.dateRange || '-'}
            </Typography>
          );
        }
      },
      {
        field: 'employee',
        headerName: t('plannerMessages.columns.employee'),
        flex: 1.2,
        minWidth: 150,
        valueGetter: (_value: unknown, row: ParsedPlannerMessageRow) =>
          row.parsed.employeeName || '',
        renderCell: ({
          row
        }: GridRenderCellParams<ParsedPlannerMessageRow>) => {
          if (!row.parsed.employeeName) return null;
          return (
            <EmployeeCell>
              <EmployeeAvatar>
                {getInitials(row.parsed.employeeName)}
              </EmployeeAvatar>
              <Typography variant="body2" noWrap>
                {row.parsed.employeeName}
              </Typography>
            </EmployeeCell>
          );
        }
      },
      {
        field: 'taskDefinitionKey',
        headerName: t('plannerMessages.columns.type'),
        flex: 1.2,
        minWidth: 120,
        valueGetter: (_value: unknown, row: ParsedPlannerMessageRow) =>
          row.parsed.taskTypeLabel || '',
        renderCell: ({
          row
        }: GridRenderCellParams<ParsedPlannerMessageRow>) => {
          const isRejected = REJECTION_TASK_KEYS.includes(
            row.taskDefinitionKey as (typeof REJECTION_TASK_KEYS)[number]
          );
          return (
            <Typography
              variant="body2"
              noWrap
              color={isRejected ? 'error' : 'text.primary'}
            >
              {row.parsed.taskTypeLabel}
            </Typography>
          );
        }
      },
      {
        field: 'leaveType',
        headerName: t('plannerMessages.columns.leaveType'),
        flex: 1,
        minWidth: 120,
        valueGetter: (_value: unknown, row: ParsedPlannerMessageRow) =>
          row.parsed.leaveType || '',
        renderCell: ({
          row
        }: GridRenderCellParams<ParsedPlannerMessageRow>) => {
          return (
            <Typography variant="body2" noWrap>
              {row.parsed.leaveType || '-'}
            </Typography>
          );
        }
      },
      {
        field: 'planUnit',
        headerName: t('plannerMessages.columns.planUnit'),
        flex: 1.2,
        minWidth: 120,
        valueGetter: (_value: unknown, row: ParsedPlannerMessageRow) =>
          row.parsed.planUnitChange || row.parsed.planUnit || '',
        renderCell: ({
          row
        }: GridRenderCellParams<ParsedPlannerMessageRow>) => {
          return (
            <Typography variant="body2" noWrap>
              {row.parsed.planUnitChange || row.parsed.planUnit || '-'}
            </Typography>
          );
        }
      },
      {
        field: 'note',
        headerName: t('plannerMessages.columns.note'),
        flex: 1,
        minWidth: 100,
        valueGetter: (_value: unknown, row: ParsedPlannerMessageRow) =>
          row.parsed.note || '',
        renderCell: ({
          row
        }: GridRenderCellParams<ParsedPlannerMessageRow>) => {
          return (
            <NoteText>
              <Typography variant="body2" noWrap title={row.parsed.note}>
                {row.parsed.note || '-'}
              </Typography>
            </NoteText>
          );
        }
      },
      {
        field: 'createTime',
        headerName: t('plannerMessages.columns.created'),
        type: 'dateTime',
        flex: 1,
        minWidth: 100,
        valueGetter: (_value: unknown, row: ParsedPlannerMessageRow) =>
          row.createTime ? new Date(row.createTime) : null,
        renderCell: ({
          row
        }: GridRenderCellParams<ParsedPlannerMessageRow>) => {
          return (
            <Typography variant="body2" noWrap>
              {row.parsed.createdAt}
            </Typography>
          );
        }
      },
      {
        field: 'dutyCodeChange',
        headerName: t('plannerMessages.columns.dutyCodeChange'),
        flex: 1,
        minWidth: 120,
        valueGetter: (_value: unknown, row: ParsedPlannerMessageRow) =>
          row.parsed.dutyCodeChange ?? '',
        renderCell: ({
          row
        }: GridRenderCellParams<ParsedPlannerMessageRow>) => {
          const change = row.parsed.dutyCodeChange;
          if (!change) return null;
          return (
            <Typography variant="body2" noWrap>
              {change}
            </Typography>
          );
        }
      },
      {
        field: 'hoursDiff',
        headerName: t('plannerMessages.columns.hoursDiff'),
        flex: 0.6,
        minWidth: 80,
        type: 'number',
        headerAlign: 'left',
        align: 'left',
        valueGetter: (_value: unknown, row: ParsedPlannerMessageRow) =>
          row.parsed.hoursDiff ?? null,
        renderCell: ({
          row
        }: GridRenderCellParams<ParsedPlannerMessageRow>) => {
          const { hoursDiff, newHours } = row.parsed;
          const isNewDuty = row.taskDefinitionKey.includes('Create');
          const parts: ReactNode[] = [];

          if (isNewDuty && newHours !== null && newHours !== undefined) {
            parts.push(
              <PillChip
                key="newHours"
                label={`${newHours}u`}
                size="small"
                variant="tonal"
              />
            );
          }

          if (hoursDiff && Math.abs(hoursDiff) >= 0.01) {
            const label = hoursDiff > 0 ? `+${hoursDiff}` : String(hoursDiff);
            const color = hoursDiff > 0 ? 'warning' : 'default';
            parts.push(
              <PillChip
                key="diff"
                label={label}
                color={color}
                size="small"
                variant="tonal"
              />
            );
          }

          if (parts.length === 0) return null;
          return <PillChipGroup>{parts}</PillChipGroup>;
        }
      },
      {
        field: 'actions',
        headerName: t('plannerMessages.columns.actions'),
        flex: 0.8,
        minWidth: 100,
        sortable: false,
        filterable: false,
        headerAlign: 'left',
        align: 'left',
        renderCell: ({
          row
        }: GridRenderCellParams<ParsedPlannerMessageRow>) => {
          const leaveRequestId = row.taskVariables?.find(
            (v) => v.name === 'leaveRequestId'
          )?.value;
          return (
            <ActionsCell onClick={(e) => e.stopPropagation()}>
              {row.active && (
                <MessageActionButtons
                  taskId={row.taskId}
                  taskDefinitionKey={row.taskDefinitionKey}
                  leaveRequestId={leaveRequestId}
                  leaveType={row.parsed.leaveType}
                  actions={row.actions}
                />
              )}
              <Tooltip title={t('plannerMessages.actions.delete')}>
                <ActionButton
                  size="small"
                  onClick={() => deleteTask(row.processInstanceId)}
                >
                  <Icon type="trash-can" fontSize="small" />
                </ActionButton>
              </Tooltip>
            </ActionsCell>
          );
        }
      }
    ],
    [t, deleteTask]
  );

  const getDetailPanelContent = useCallback(
    (params: GridRowParams<ParsedPlannerMessageRow>) => (
      <AnimatedDetailPanel html={params.row.message?.body || ''} />
    ),
    []
  );

  const getDetailPanelHeight = useCallback(() => 'auto' as const, []);

  const handleRowClick = useCallback(
    (params: GridRowParams<ParsedPlannerMessageRow>) => {
      apiRef.current?.toggleDetailPanel(params.id);
    },
    [apiRef]
  );

  const handlePaginationModelChange = (model: {
    page: number;
    pageSize: number;
  }) => {
    setPageIndex(model.page);
  };

  const handleSelectionChange = (
    selectionModel: { ids: Set<string> } | unknown
  ) => {
    clearSelection();
    const ids =
      selectionModel &&
      typeof selectionModel === 'object' &&
      'ids' in selectionModel
        ? (selectionModel.ids as Set<string>)
        : new Set<string>();

    if (ids.size > 0) {
      const taskIds = parsedMessages
        .filter((m) => ids.has(m.processInstanceId))
        .map((m) => m.taskId);
      selectAllTasks(taskIds);
    }
  };

  const isRowSelectable = useCallback(
    (params: GridRowParams<ParsedPlannerMessageRow>) =>
      params.row.active && !!params.row.actions?.length,
    []
  );

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries('plannerTasks');
    queryClient.invalidateQueries('plannerTaskTabCounts');
  }, [queryClient]);

  return (
    <ToolbarWrapper
      planUnitSelector={{
        onSelect: handlePlanUnitChange,
        selectedValues: lastUsedPlanUnits?.length
          ? lastUsedPlanUnits
          : undefined,
        hideGroups: true
      }}
      dateNavigation={{
        showDateNavigation: true,
        selectedDate:
          period !== 'custom' && startDate ? parseISO(startDate) : null,
        selectedDateRange:
          period === 'custom'
            ? [
                startDate ? parseISO(startDate) : null,
                endDate ? parseISO(endDate) : null
              ]
            : undefined,
        onSelectedDate: handleDateChange,
        dateNavigationOptions: ['month', 'year', 'custom'],
        allowDateNavigationMenuOptions: true
      }}
      actions={[
        {
          tooltipLabel: t('employeeMessages.sync'),
          id: 'planner-messages-refresh',
          icon: 'arrows-repeat',
          onClick: handleRefresh,
          alignment: 'right'
        }
      ]}
    >
      <MessagesContainer>
        <TaskTypeTabs messages={messages} />

        <GridWrapper>
          <DataGrid<ParsedPlannerMessageRow>
            rows={parsedMessages}
            columns={columns}
            loading={isLoading}
            apiRef={apiRef}
            getRowId={(row) => row.processInstanceId}
            checkboxSelection={!isBulkDisabled}
            isRowSelectableFromParent={isRowSelectable}
            onSelectionChange={handleSelectionChange}
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            getDetailPanelContent={getDetailPanelContent}
            getDetailPanelHeight={getDetailPanelHeight}
            columnVisibilityModel={columnVisibilityModel}
            pagination
            paginationMode="server"
            page={pageIndex}
            pageSize={PAGE_SIZE}
            pageSizeOptions={[PAGE_SIZE]}
            rowCount={totalCount}
            onPaginationModelChange={handlePaginationModelChange}
            shouldRenderToolBar={false}
          />
        </GridWrapper>

        <LoadingOverlay loading={isLoading} />
      </MessagesContainer>
    </ToolbarWrapper>
  );
};

export default PlannerMessages;
